/**
 * 浮层共用行为的唯一出处：焦点圈 / ESC / 焦点还原 / body 滚动锁（外点关闭由组件自理）。
 * Modal / Dialog / Drawer / AppShell 抽屉一律从这里取，禁止在组件里复制实现。
 *
 * z-index 分层约定（全包统一，改动须同步本注释）：
 *   z-40    页面级吸顶 chrome（AppShell 移动顶栏），恒在遮罩之下；
 *   z-50    遮罩+面板（Modal/Dialog/Drawer/AppShell 抽屉）与锚定浮层（DropdownMenu/Tooltip）——
 *           同层叠加靠 DOM 挂载顺序，模态内嵌的浮层随面板 stacking context 天然在上；
 *   z-[60]  全局通知 ToastStack，永远最上——模态之上的操作反馈也必须可见。
 *
 * 进场过渡约定（useEnterTransition + overlayBackdropTransition/overlayPanelTransition）：
 * 150ms、仅 opacity/transform，全部经 motion-safe 门控，prefers-reduced-motion 下即时显形；
 * 出场不做延迟卸载，保持 open=false 立即移除 DOM 的既有契约。
 * 进场终态（entered=true）一律输出空串、不残留任何 transform 类：元素上恒挂非 none 的
 * transform 会按 CSS Transforms L2 成为 fixed 后代的包含块，面板内嵌套的 ConfirmDialog
 * （fixed 定位）会相对面板错位并被 overflow-hidden 裁切（返工 M1，2026-08-26 Opus 审核）。
 */
import { useEffect, useRef, useState } from 'react'

const focusableSelector = 'input, select, textarea, button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'

/** 遮罩进场过渡类：150ms 仅 opacity。 */
export const overlayBackdropTransition = 'motion-safe:transition-opacity motion-safe:duration-150'
/** 面板进场过渡类：150ms 仅 opacity/transform。 */
export const overlayPanelTransition = 'motion-safe:transition-[opacity,transform] motion-safe:duration-150'

/* 滚动锁用计数而非逐实例存取：叠层（模态上再开模态）时内层先关不会提前解锁。 */
let scrollLockCount = 0
let savedOverflow: string | null = null

/* ESC 分层实例栈：打开时入栈、关闭时出栈，同一时刻只有栈顶（最后打开）的浮层响应 ESC；
 * 嵌套场景（模态上再开模态）一次 ESC 只关顶层，顶层卸载出栈后下一次按键才落到下一层。 */
const escStack: symbol[] = []

function lockBodyScroll() {
  if (scrollLockCount++ === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
}

function unlockBodyScroll() {
  if (--scrollLockCount === 0) {
    document.body.style.overflow = savedOverflow ?? ''
    savedOverflow = null
  }
}

/* 无 rAF 的环境（测试）退化为宏任务，语义不变：下一拍再置 entered。 */
function nextFrame(callback: () => void): () => void {
  if (typeof requestAnimationFrame === 'function') {
    const id = requestAnimationFrame(callback)
    return () => cancelAnimationFrame(id)
  }
  const id = setTimeout(callback, 16)
  return () => clearTimeout(id)
}

export type OverlayOptions = {
  /** 打开时的显式初始焦点；优先级高于面板内 [data-dialog-initial-focus] 与首个可聚焦元素 */
  initialFocusRef?: { current: HTMLElement | null }
}

export function useOverlay<T extends HTMLElement>(open: boolean, onClose: () => void, options?: OverlayOptions) {
  const panelRef = useRef<T>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    // initialFocusRef 是 ref 容器，在 open 翻正这一拍读取即可，不进依赖
    ;(options?.initialFocusRef?.current ?? panel?.querySelector<HTMLElement>('[data-dialog-initial-focus]') ?? panel?.querySelector<HTMLElement>(focusableSelector))?.focus()
    const stackId = Symbol('overlay')
    escStack.push(stackId)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // 非栈顶不响应：等顶层关闭出栈后，下一次 ESC 才轮到自己
        if (escStack[escStack.length - 1] !== stackId) return
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !panel) return
      const elements = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector))
      if (!elements.length) {
        // 面板内没有可聚焦元素也不能让 Tab 逃逸；面板有 tabIndex 时聚焦面板自身
        event.preventDefault()
        panel.focus()
        return
      }
      const first = elements[0]!
      const last = elements[elements.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    lockBodyScroll()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const stackIndex = escStack.indexOf(stackId)
      if (stackIndex >= 0) escStack.splice(stackIndex, 1)
      unlockBodyScroll()
      previousFocus?.focus()
    }
  }, [open])

  return panelRef
}

/**
 * 进场过渡开关：open 翻正后下一拍返回 true，组件用它切换 opacity/transform 终态类；
 * 过渡本身由 motion-safe 类门控，reduced-motion 下终态类照样应用但无过渡，等效即时显形。
 */
export function useEnterTransition(open: boolean) {
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    if (!open) {
      setEntered(false)
      return
    }
    return nextFrame(() => setEntered(true))
  }, [open])
  return entered
}
